import { supabase } from '@/integrations/supabase/client';
import { NoteType } from '@/types/notes';
import { Json } from '@/integrations/supabase/types';

export interface AnalysisSession {
  id: string;
  user_id: string;
  name: string;
  description?: string;
  note_space_ids: string[];
  canvas_data: Json;
  created_at: string;
  updated_at: string;
}

export interface AnalysisCluster {
  id: string;
  analysis_session_id: string;
  name: string;
  description?: string;
  color: string;
  position_x: number;
  position_y: number;
  width: number;
  height: number;
  note_ids: string[];
  created_at: string;
  updated_at: string;
}

export interface AnalysisNote {
  id: string;
  content: string;
  type: NoteType;
  highlighted: boolean;
  source_interview_id: string;
  source_interview_name: string;
  position_x: number;
  position_y: number;
  created_at: string;
}

// Analysis Session operations
export const analysisSessionService = {
  async create(name: string, noteSpaceIds: string[], description?: string): Promise<AnalysisSession> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase
      .from('analysis_sessions')
      .insert({ 
        name, 
        description,
        note_space_ids: noteSpaceIds,
        user_id: user.id 
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getAll(): Promise<AnalysisSession[]> {
    const { data, error } = await supabase
      .from('analysis_sessions')
      .select('*')
      .order('updated_at', { ascending: false });
    
    if (error) throw error;
    return data || [];
  },

  async getById(id: string): Promise<AnalysisSession | null> {
    const { data, error } = await supabase
      .from('analysis_sessions')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    
    if (error) throw error;
    return data;
  },

  async update(id: string, updates: Partial<Pick<AnalysisSession, 'name' | 'description'>> & { canvas_data?: Json }): Promise<AnalysisSession> {
    const { data, error } = await supabase
      .from('analysis_sessions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('analysis_sessions')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Analysis Cluster operations
export const analysisClusterService = {
  async create(sessionId: string, clusterData: {
    name: string;
    description?: string;
    color?: string;
    position_x: number;
    position_y: number;
    width?: number;
    height?: number;
    note_ids?: string[];
  }): Promise<AnalysisCluster> {
    const { data, error } = await supabase
      .from('analysis_clusters')
      .insert({
        analysis_session_id: sessionId,
        ...clusterData
      })
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async getBySessionId(sessionId: string): Promise<AnalysisCluster[]> {
    const { data, error } = await supabase
      .from('analysis_clusters')
      .select('*')
      .eq('analysis_session_id', sessionId)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    return data || [];
  },

  async update(id: string, updates: Partial<Omit<AnalysisCluster, 'id' | 'analysis_session_id' | 'created_at' | 'updated_at'>>): Promise<AnalysisCluster> {
    const { data, error } = await supabase
      .from('analysis_clusters')
      .update(updates)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;
    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('analysis_clusters')
      .delete()
      .eq('id', id);
    
    if (error) throw error;
  }
};

// Utility function to get notes from selected interviews
// Generate simple hash from note IDs and note space last modified time
const generateSimpleHash = (noteIds: string[], noteSpaceIds: string[], lastModified?: string): string => {
  const sortedNoteIds = [...noteIds].sort();
  const sortedSpaceIds = [...noteSpaceIds].sort();
  
  const hashData = {
    noteIds: sortedNoteIds,
    noteSpaceIds: sortedSpaceIds,
    lastModified: lastModified || 'unknown'
  };
  
  return btoa(JSON.stringify(hashData));
};

// Analysis Cache operations
export const analysisCacheService = {
  async getCachedAnalysis(noteIds: string[], noteSpaceIds: string[], lastModified?: string, questions?: string[]): Promise<any | null> {
    try {
      const cacheKey = generateSimpleHash(noteIds, noteSpaceIds, lastModified);
      console.log('🔍 Generated simple cache key:', cacheKey);
      console.log('📋 Note IDs:', noteIds.length, 'Space IDs:', noteSpaceIds);
      
      const { data, error } = await supabase
        .from('analysis_cache')
        .select('cached_results, created_at, notes_hash')
        .contains('note_space_ids', noteSpaceIds)
        .eq('notes_hash', cacheKey)
        .gte('created_at', new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString())
        .maybeSingle();
      
      console.log('💾 Cache lookup result:', data ? 'FOUND' : 'NOT FOUND', error ? `ERROR: ${error.message}` : '');
      
      if (error || !data) {
        return null;
      }
      
      console.log('🎯 Cache hit! Using cached results');
      return data.cached_results;
    } catch (error) {
      console.error('Error getting cached analysis:', error);
      return null;
    }
  },

  async setCachedAnalysis(noteIds: string[], noteSpaceIds: string[], results: any, lastModified?: string, questions?: string[]): Promise<void> {
    try {
      const cacheKey = generateSimpleHash(noteIds, noteSpaceIds, lastModified);
      
      // Delete old cache entries for these note spaces
      await supabase
        .from('analysis_cache')
        .delete()
        .contains('note_space_ids', noteSpaceIds);
      
      // Create new cache entry
      await supabase
        .from('analysis_cache')
        .insert({
          note_space_ids: noteSpaceIds,
          notes_hash: cacheKey,
          questions_hash: questions ? btoa(JSON.stringify(questions.sort())) : null,
          cached_results: results
        });
      
      console.log('💾 Cached analysis results with simple hash');
    } catch (error) {
      console.error('❌ Error caching analysis:', error);
    }
  }
};

export const getNotesFromInterviews = async (noteSpaceIds: string[]): Promise<AnalysisNote[]> => {
  // Get all note spaces in one query
  const { data: noteSpaces, error: spacesError } = await supabase
    .from('note_spaces')
    .select('id, name')
    .in('id', noteSpaceIds);
  
  if (spacesError || !noteSpaces) return [];
  
  // Get all sticky notes from all spaces in one query
  const { data: flowNodes, error: nodesError } = await supabase
    .from('flow_nodes')
    .select('*')
    .in('note_space_id', noteSpaceIds)
    .eq('node_type', 'stickyNote');
  
  if (nodesError || !flowNodes) return [];
  
  // Create a map of space ID to space name for efficiency
  const spaceNameMap = new Map(noteSpaces.map(space => [space.id, space.name]));
  
  // Convert to AnalysisNote format
  const allNotes: AnalysisNote[] = flowNodes
    .filter(node => node.data && typeof node.data === 'object' && node.data !== null && 'content' in node.data)
    .map(node => {
      const nodeData = node.data as Record<string, any>;
      return {
        id: node.id,
        content: nodeData.content as string,
        type: (nodeData.type as NoteType) || 'note',
        highlighted: (nodeData.highlighted as boolean) || false,
        source_interview_id: node.note_space_id,
        source_interview_name: spaceNameMap.get(node.note_space_id) || 'Unknown Interview',
        position_x: node.position_x,
        position_y: node.position_y,
        created_at: node.created_at,
      };
    });
  
  return allNotes;
};